<script>
import Section from "../../components/PageSection";

export default {
  name: "Fusion",
  components: { Section },
  data() {
    return {
      projets: [],
      phases: [],

      // ORIGINE
      originProjetId: "",
      originPhaseId: "",

      // DESTINATION
      destProjetId: "",
      destPhaseId: "",

      // PREVIEW UI
      previewLoading: false,
      previewError: "",
      previewResult: null, // { ok: true, counts: {...} }

      // EXECUTE UI
      executeLoading: false,
      executeError: "",
      executeResult: null, // { ok, mergeId, counts }
    };
  },
  mounted() {
    this.$axios
      .get("projets/actives/" + this.$store.state.auth.currentAgence._id)
      .then((response) => (this.projets = response.data))
      .catch((e) => console.log(e));

    this.$axios
      .get(
        "saisieTemps/phasesActivesByAgence/" +
          this.$store.state.auth.currentAgence._id,
      )
      .then((response) => (this.phases = response.data))
      .catch((e) => console.log(e));
  },
  computed: {
    originPhasesSelect() {
      if (!this.originProjetId) return [];
      return this.phases.filter((p) => p.projetId === this.originProjetId);
    },
    destPhasesSelect() {
      if (!this.destProjetId) return [];
      return this.phases.filter((p) => p.projetId === this.destProjetId);
    },

    previewCountsList() {
      const counts = this.previewResult?.counts;
      if (!counts) return [];

      return Object.entries(counts)
        .filter(([key]) => key !== "tempsjoursTotal") // ✅ on exclut la somme
        .map(([key, value]) => ({ key, value: Number(value) || 0 }))
        .sort((a, b) => b.value - a.value);
    },

    previewTotal() {
      return this.previewCountsList.reduce((sum, item) => sum + item.value, 0);
    },
  },
  watch: {
    originProjetId() {
      this.originPhaseId = "";
      this.resetPreview();
    },
    destProjetId() {
      this.destPhaseId = "";
      this.resetPreview();
    },
    originPhaseId() {
      this.resetPreview();
    },
    destPhaseId() {
      this.resetPreview();
    },
  },
  methods: {
    resetPreview() {
      this.previewResult = null;
      this.previewError = "";
      this.previewLoading = false;
      this.executeResult = null;
      this.executeError = "";
      this.executeLoading = false;
    },

    projetLabel(p) {
      return p.nom || p.name || p._id;
    },
    phaseLabel(ph) {
      return ph.nom || ph.name || ph._id;
    },

    labelForCountKey(key) {
      // Affichage plus lisible (optionnel)
      const map = {
        contractualises: "Contractualisés",
        facture_lignes: "Lignes de facture",
        notedefrais: "Notes de frais",
        notedeprestas: "Notes de prestas",
        tempsheures: "Temps (heures)",
        tempsjours: "Temps (jours)",
      };
      return map[key] || key;
    },

    async onPreview() {
      try {
        this.previewLoading = true;
        this.previewError = "";
        this.previewResult = null;

        const response = await this.$axios.post("/phaseFusion/preview", {
          agenceId: this.$store.state.auth.currentAgence._id,
          originPhaseId: this.originPhaseId,
          destPhaseId: this.destPhaseId,
        });

        this.previewResult = response.data;
      } catch (error) {
        console.error(error);
        this.previewError =
          error?.response?.data?.message || "Erreur lors du dry run (preview).";
      } finally {
        this.previewLoading = false;
      }
    },

    resetExecute() {
      this.executeLoading = false;
      this.executeError = "";
      this.executeResult = null;
    },

    async onFusionner() {
      if (!this.originPhaseId || !this.destPhaseId) return;
      if (this.originPhaseId === this.destPhaseId) return;

      try {
        this.executeLoading = true;
        this.executeError = "";
        this.executeResult = null;

        const response = await this.$axios.post("/phaseFusion/execute", {
          agenceId: this.$store.state.auth.currentAgence._id,
          originPhaseId: this.originPhaseId,
          destPhaseId: this.destPhaseId,
        });

        this.executeResult = response.data;

        // Optionnel : rafraîchir le dry run pour montrer que l'origine est à 0
        await this.onPreview();
      } catch (error) {
        console.error(error);
        this.executeError =
          error?.response?.data?.message || "Erreur lors de la fusion.";
      } finally {
        this.executeLoading = false;
      }
    },
  },
};
</script>

<template>
  <div style="width: 700px">
    <Section text="Phase d'origine">
      <template v-slot:body>
        <div class="content">
          <div class="content__text">
            Sélectionner le projet et la phase d'origine :
          </div>

          <div class="content__list">
            <div class="content__list__item">
              <select v-model="originProjetId" class="inline-block">
                <option value="" disabled>Choisir un projet</option>
                <option v-for="p in projets" :key="p._id" :value="p._id">
                  {{ projetLabel(p) }}
                </option>
              </select>

              <select
                v-model="originPhaseId"
                class="inline-block"
                :disabled="!originProjetId"
              >
                <option value="" disabled>Choisir une phase</option>
                <option
                  v-for="ph in originPhasesSelect"
                  :key="ph._id"
                  :value="ph._id"
                >
                  {{ phaseLabel(ph) }}
                </option>
              </select>
            </div>
          </div>
        </div>
      </template>
    </Section>

    <Section text="Phase de destination">
      <template v-slot:body>
        <div class="content">
          <div class="content__text">
            Sélectionner le projet et la phase de destination :
          </div>

          <div class="content__list">
            <div class="content__list__item">
              <select v-model="destProjetId" class="inline-block">
                <option value="" disabled>Choisir un projet</option>
                <option v-for="p in projets" :key="p._id" :value="p._id">
                  {{ projetLabel(p) }}
                </option>
              </select>

              <select
                v-model="destPhaseId"
                class="inline-block"
                :disabled="!destProjetId"
              >
                <option value="" disabled>Choisir une phase</option>
                <option
                  v-for="ph in destPhasesSelect"
                  :key="ph._id"
                  :value="ph._id"
                >
                  {{ phaseLabel(ph) }}
                </option>
              </select>
            </div>
          </div>
        </div>
      </template>
    </Section>

    <button
      class="button"
      @click="onPreview"
      :disabled="
        previewLoading ||
        executeLoading ||
        !originPhaseId ||
        !destPhaseId ||
        originPhaseId === destPhaseId
      "
    >
      {{ previewLoading ? "Dry Run..." : "Dry Run" }}
    </button>

    <button
      class="button"
      @click="onFusionner"
      :disabled="
        executeLoading ||
        !originPhaseId ||
        !destPhaseId ||
        originPhaseId === destPhaseId
      "
    >
      {{ executeLoading ? "Fusion en cours..." : "Fusionner" }}
    </button>

    <!-- ✅ Bloc d'info sous les boutons -->
    <!-- ✅ État fusion (UX) -->
    <div v-if="executeLoading" class="infoBox infoBox--loading">
      <div class="infoBox__title">Fusion en cours…</div>
      <div class="infoBox__hint">
        Réaffectation des éléments de la phase d’origine vers la phase de
        destination.
      </div>
    </div>

    <div v-else-if="executeError" class="infoBox infoBox--error">
      <div class="infoBox__title">Échec de la fusion</div>
      <div class="infoBox__hint">{{ executeError }}</div>
    </div>

    <div
      v-else-if="executeResult && executeResult.ok"
      class="infoBox infoBox--success"
    >
      <div class="infoBox__title">Fusion terminée ✅</div>

      <div class="infoBox__hint" v-if="executeResult.mergeId">
        Identifiant de fusion : <strong>{{ executeResult.mergeId }}</strong>
      </div>

      <div class="infoBox__list" v-if="executeResult.counts">
        <div
          v-for="(value, key) in executeResult.counts"
          :key="key"
          class="infoBox__row"
        >
          <div class="infoBox__label">{{ labelForCountKey(key) }}</div>
          <div class="infoBox__value">{{ value }}</div>
        </div>
      </div>

      <div class="infoBox__hint">
        Tu peux relancer un dry run pour vérifier que la phase d’origine est
        vide.
      </div>
    </div>

    <div v-if="previewLoading" class="infoBox infoBox--loading">
      Analyse en cours… (dry run)
    </div>

    <div v-else-if="previewError" class="infoBox infoBox--error">
      {{ previewError }}
    </div>

    <div
      v-else-if="previewResult && previewResult.ok"
      class="infoBox infoBox--success"
    >
      <div class="infoBox__title">
        Dry run terminé — {{ previewTotal }} élément(s) seraient réaffecté(s)
      </div>

      <div class="infoBox__list">
        <div
          v-for="item in previewCountsList"
          :key="item.key"
          class="infoBox__row"
        >
          <div class="infoBox__label">{{ labelForCountKey(item.key) }}</div>
          <div class="infoBox__value">
            <template v-if="item.key === 'tempsjours'">
              {{ item.value }}
              <span v-if="previewResult.counts.tempsjoursTotal">
                ({{ previewResult.counts.tempsjoursTotal }} j.)
              </span>
            </template>
            <template v-else>
              {{ item.value }}
            </template>
          </div>
        </div>
      </div>

      <div class="infoBox__hint">
        Rien n’a été modifié (prévisualisation uniquement).
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "../../assets/main.scss";

.inline-block {
  display: inline-block;
  @include myBlock;
  width: 48%;
}

.content {
  display: flex;
  justify-content: space-between;
  margin: 0 0 50px 0;

  &__text {
    @include myFont;
    font-size: 14px;
    width: 400px;
    padding: 0 20px 0 0;
    color: $black-bolder;
  }

  &__list {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin: 0 0 25px 0;
    flex: 1;

    &__item {
      width: 100%;
      display: flex;
      justify-content: space-between;
      gap: 10px;
    }
  }
}

.button {
  @include myButton;
  margin: 0 10px 15px 0;
}

/* Bloc info */
.infoBox {
  @include myBlock;
  padding: 15px;
  margin-top: 10px;

  &__title {
    @include myFont;
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 10px;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 10px;
  }

  &__row {
    display: flex;
    justify-content: space-between;
    gap: 10px;
  }

  &__label {
    @include myFont;
    font-size: 13px;
    color: $black-bolder;
  }

  &__value {
    @include myFont;
    font-size: 13px;
    font-weight: 600;
    color: $black-bolder;
  }

  &__hint {
    @include myFont;
    font-size: 12px;
    opacity: 0.8;
  }

  &--loading {
    opacity: 0.9;
  }

  &--error {
    border-left: 4px solid #d33;
  }

  &--success {
    border-left: 4px solid #2a7;
  }
}
</style>
